import { useEffect, useRef } from 'react';

/**
 * Reinicia uma animação CSS **sem remontar nada** (spec-calendar-ux.md §9).
 *
 * A mecânica é a mesma que o `AppShell` usa desde a primeira rodada, extraída
 * agora que ela tem três chamadores: tirar a classe, forçar o refluxo, repor.
 *
 * **Por que não `key={algo}`.** Seria uma linha em vez de dez, e destruiria a
 * árvore inteira a cada mudança. No `AppShell` isso significaria perder o que a
 * pessoa está digitando no formulário; no calendário significaria o FullCalendar
 * perder a posição, a vista e o mês — a grade voltaria para hoje toda vez que a
 * pessoa trocasse de período, que é literalmente o oposto do que ela pediu.
 *
 * **Por que `usePageTransition` NÃO foi refatorado para usar isto.** Ele carrega
 * uma regra que é só dele — navegação `REPLACE` não anima, porque o rascunho que
 * nasce troca a URL sem ninguém ter pedido tela nova — e espremer essa regra
 * aqui obrigaria este hook a conhecer o React Router. Duplicar seis linhas custa
 * menos do que uma abstração que precisa saber de rota.
 *
 * ⚠️ **`duration-*` e `animate-*` no mesmo elemento se atropelam**: o
 * `tailwindcss-animate` emite `duration` como `transition-duration` E como
 * `animation-duration`. Aplique a classe numa `div` de invólucro sem classe de
 * duração, nunca no elemento que já tem `transition`.
 *
 * O bloco global de `prefers-reduced-motion` (`styles/index.css`) já zera tudo
 * isto — quem desligou o movimento continua lendo o mesmo painel.
 *
 * @param className a classe de animação (ex.: `animate-rise-in`)
 * @param trigger   muda → a animação roda de novo. Não roda na primeira pintura:
 *                  a classe já está no JSX, e reiniciar ali a rodaria duas vezes.
 */
export function useReplayAnimation<T extends HTMLElement>(className: string, trigger: unknown) {
  const ref = useRef<T>(null);
  const isFirstRender = useRef(true);
  /**
   * A classe que ESTE hook pôs por último.
   *
   * Sem ela, trocar de animação (deslizar para a direita → deslizar para a
   * esquerda) deixaria a anterior grudada no nó: `remove(className)` só tira a
   * nova, e o elemento acumularia as duas — que é como uma grade acaba andando
   * para os dois lados ao mesmo tempo.
   */
  const appliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      appliedRef.current = className;
      return;
    }

    const node = ref.current;
    if (!node) return;

    if (appliedRef.current) node.classList.remove(appliedRef.current);
    node.classList.remove(className);
    node.getBoundingClientRect(); // força o refluxo que rearma a animação
    node.classList.add(className);
    appliedRef.current = className;
  }, [className, trigger]);

  return ref;
}
